import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react';

interface AccessibilityContextValue {
  isDarkMode: boolean;
  isHighContrast: boolean;
  isLargeFont: boolean;
  isScreenReaderOn: boolean;
  toggleDarkMode: () => void;
  toggleHighContrast: () => void;
  toggleLargeFont: () => void;
  toggleScreenReader: () => void;
  speak: (text: string) => void;
}

const AccessibilityContext = createContext<AccessibilityContextValue | null>(null);

export function AccessibilityProvider({ children }: { children: ReactNode }) {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [isHighContrast, setIsHighContrast] = useState(() => {
    const saved = localStorage.getItem('highContrast');
    return saved ? JSON.parse(saved) : false;
  });
  const [isLargeFont, setIsLargeFont] = useState(() => {
    const saved = localStorage.getItem('largeFont');
    return saved ? JSON.parse(saved) : false;
  });
  const [isScreenReaderOn, setIsScreenReaderOn] = useState(() => {
    const saved = localStorage.getItem('screenReader');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  useEffect(() => {
    if (isHighContrast) {
      document.documentElement.classList.add('high-contrast');
    } else {
      document.documentElement.classList.remove('high-contrast');
    }
    localStorage.setItem('highContrast', JSON.stringify(isHighContrast));
  }, [isHighContrast]);

  useEffect(() => {
    if (isLargeFont) {
      document.documentElement.classList.add('large-font');
    } else {
      document.documentElement.classList.remove('large-font');
    }
    localStorage.setItem('largeFont', JSON.stringify(isLargeFont));
  }, [isLargeFont]);

  useEffect(() => {
    localStorage.setItem('screenReader', JSON.stringify(isScreenReaderOn));

    if (isScreenReaderOn) {
      document.documentElement.setAttribute('aria-live', 'polite');
    } else {
      document.documentElement.removeAttribute('aria-live');
    }
  }, [isScreenReaderOn]);

  const toggleDarkMode = () => setIsDarkMode((prev) => !prev);
  const toggleHighContrast = () => setIsHighContrast((prev) => !prev);
  const toggleLargeFont = () => setIsLargeFont((prev) => !prev);
  const toggleScreenReader = () => setIsScreenReaderOn((prev) => !prev);

  const speak = (text: string) => {
    if (!isScreenReaderOn) return;

    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'pt-BR';
      utterance.rate = 0.8;
      utterance.pitch = 1;
      window.speechSynthesis.speak(utterance);
    }
  };

  const value: AccessibilityContextValue = {
    isDarkMode,
    isHighContrast,
    isLargeFont,
    isScreenReaderOn,
    toggleDarkMode,
    toggleHighContrast,
    toggleLargeFont,
    toggleScreenReader,
    speak,
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility deve ser usado dentro de AccessibilityProvider');
  }
  return context;
};
